import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import './Card.css';

type CardVariant = 'glass' | 'opaque';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  ambient?: boolean;
  children?: ReactNode;
}

/**
 * Card — the surface primitive.
 *
 * Pitch Glass rule (docs/ground-up/04_visual_language_pitch_glass.md §3):
 * **glass on chrome, never on data.** Use `variant="glass"` only for chrome —
 * modals, popovers, the Brain / AI right-rail, generated-plan previews,
 * floating panels. Use `variant="opaque"` (the default) for dense data
 * surfaces — list rows, tables, the calendar grid, the drill-designer working
 * area — where legibility under cold-hands-and-rain conditions must win.
 * When in doubt, prefer opaque.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'opaque', ambient = false, className = '', children, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      data-variant={variant}
      data-ambient={ambient || undefined}
      className={`card ${className}`.trim()}
      {...rest}
    >
      {children}
    </div>
  );
});
